<div class="plv" #host role="group" [attr.aria-label]="ariaLabel()">
  @for (group of renderGroups(); track group.key) {
    <div class="plv__group" [class.plv__group--muted]="group.muted">
      <div class="plv__group-header">
        <span class="plv__group-dot" [style.background]="group.color || 'var(--amber)'" aria-hidden="true"></span>
        <span class="plv__group-title">{{ group.label }}</span>
        @if (group.hint) {
          <span class="plv__group-hint">{{ group.hint }}</span>
        }
      </div>

      <!-- Kolomkoppen: de eigenschap, gedraaid zodat ze in smalle kolommen passen. -->
      <div class="plv__headers" [style.height.px]="headerHeight" [style.padding-left.px]="rulerWidth">
        @for (column of group.columns; track column.key) {
          <div class="plv__header" [style.width.px]="column.widthPx" [attr.title]="column.label || column.subLabel">
            @if (column.label) {
              <span class="plv__header-text">{{ column.label }}</span>
            }
            @if (column.subLabel) {
              <span class="plv__header-sub">{{ column.subLabel }}</span>
            }
          </div>
        }
      </div>

      <div class="plv__body" [style.height.px]="heightPx()">
        <!-- Tijdas = scrubber: ticks, geboortepil en de greep van de proeftijd. -->
        <svg class="plv__ruler"
             [class.plv__ruler--draggable]="markerDraggable()"
             [attr.width]="rulerWidth"
             [attr.height]="heightPx()"
             (pointerdown)="onRulerPointerDown($event)"
             (pointermove)="onRulerPointerMove($event)"
             (pointerup)="onRulerPointerUp($event)"
             (pointercancel)="onRulerPointerCancel($event)">
          <line class="plv-axis" [attr.x1]="rulerWidth - 1" [attr.x2]="rulerWidth - 1" y1="0" [attr.y2]="heightPx()" />
          @for (tick of ticks(); track tick.yPx) {
            <line class="plv-tick" [class.plv-tick--major]="tick.major"
                  [attr.x1]="tick.major ? rulerWidth - 10 : rulerWidth - 6" [attr.x2]="rulerWidth"
                  [attr.y1]="tick.yPx" [attr.y2]="tick.yPx" />
            <text class="plv-tick-label" [class.plv-tick-label--major]="tick.major" [attr.x]="rulerWidth - 13" [attr.y]="tick.yPx + 3.5" text-anchor="end">{{ tick.label }}</text>
          }
          <g class="plv-anchor" [attr.transform]="'translate(0,' + anchorYPx() + ')'">
            <line x1="0" [attr.x2]="rulerWidth" y1="0" y2="0" />
            @if (anchorLabel()) {
              <g class="plv-pill plv-pill--anchor">
                <rect x="2" y="-17" width="46" height="15" rx="7.5" />
                <text x="25" y="-6" text-anchor="middle">{{ anchorLabel() }}</text>
              </g>
            }
          </g>
          @if (markerYPx() !== undefined) {
            <g class="plv-marker" [class.plv-marker--draggable]="markerDraggable()" [attr.transform]="'translate(0,' + markerYPx() + ')'">
              <line x1="0" [attr.x2]="rulerWidth" y1="0" y2="0" />
              <!-- Greep van 44 px hoog: dit is wat je op een telefoon vastpakt. -->
              <g class="plv-handle" data-test-id="property-lanes-marker-handle" role="slider" tabindex="-1"
                 [attr.aria-label]="markerHandleLabel()" [attr.aria-valuetext]="markerLabel()">
                <rect class="plv-handle__hit" x="0" y="-22" [attr.width]="rulerWidth" height="44" />
                <rect class="plv-handle__pill" x="3" y="-11" width="50" height="22" rx="11" />
                <text x="28" y="4" text-anchor="middle">{{ markerLabel() }}</text>
                <g class="plv-handle__grip" aria-hidden="true">
                  <line x1="18" x2="38" y1="-15" y2="-15" />
                  <line x1="18" x2="38" y1="15" y2="15" />
                </g>
              </g>
            </g>
          }
        </svg>

        <!-- Kolommen: blokken, wissellog en lijnen. Scrollen is native; tikken zet de proeftijd. -->
        <svg class="plv__columns"
             [attr.width]="columnsWidthPx()"
             [attr.height]="heightPx()"
             (click)="onColumnsClick($event)">
          @for (column of group.columns; track column.key) {
            <line class="plv-column-line" [attr.x1]="column.xPx + column.widthPx" [attr.x2]="column.xPx + column.widthPx" y1="0" [attr.y2]="heightPx()" />
            @for (item of column.blocks; track item.block.fromMs + ':' + item.xPx) {
              <g class="plv-block"
                 [class.plv-block--birth]="item.block.state === 'birth'"
                 [class.plv-block--same]="item.block.state === 'same'"
                 [class.plv-block--different]="item.block.state === 'different'"
                 [class.plv-block--accent]="item.fill != null"
                 tabindex="0" role="button"
                 [attr.aria-label]="item.block.title"
                 (click)="onBlockClick(group.key, column.laneKey, item.block, $event)"
                 (keydown)="onBlockKeydown(group.key, column.laneKey, item.block, $event)">
                <title>{{ item.block.title }}</title>
                <rect [attr.x]="item.xPx" [attr.y]="item.yPx + 0.5"
                      [attr.width]="item.widthPx" [attr.height]="item.heightPx - 1" rx="4"
                      [attr.fill]="item.fill ?? null" [attr.stroke]="item.stroke ?? null" />
                @if (item.parts.length > 0) {
                  <!-- Gedraaid langs de tijdas; de kleur inline zodat accent-blokken leesbaar blijven. -->
                  <text class="plv-block__label"
                        [attr.transform]="'translate(' + (item.xPx + item.widthPx / 2 + 3.5) + ',' + (item.yPx + item.heightPx - 5) + ') rotate(-90)'"
                        [style.fill]="item.textFill ?? null">
                    @for (part of item.parts; track $index) {
                      @if (signOf(part)) {
                        <tspan [class]="'plv-sign plv-sign--' + part.tone">{{ signOf(part) }}</tspan>
                      }
                      <tspan [class]="'plv-part plv-part--' + part.tone">{{ restOf(part) }}</tspan>
                    }
                  </text>
                }
              </g>
            }
            @for (marker of column.markers; track $index) {
              <g class="plv-marker-glyph" [attr.transform]="'translate(' + column.xPx + ',' + marker.yPx + ')'">
                <title>{{ marker.title }}</title>
                <line x1="0" [attr.x2]="column.widthPx" y1="0" y2="0" [attr.stroke]="marker.accent" />
                <text class="plv-marker-glyph__glyph" x="4" y="-3" [attr.fill]="marker.accent">{{ marker.glyph }}</text>
                <text class="plv-marker-glyph__text" x="20" y="-3" [attr.fill]="marker.accent">{{ marker.text }}</text>
              </g>
            }
          }
          <line class="plv-anchor-line" x1="0" [attr.x2]="columnsWidthPx()" [attr.y1]="anchorYPx()" [attr.y2]="anchorYPx()" />
          @if (markerYPx() !== undefined) {
            <line class="plv-marker-line" x1="0" [attr.x2]="columnsWidthPx()" [attr.y1]="markerYPx()" [attr.y2]="markerYPx()" />
          }
        </svg>
      </div>
    </div>
  }
</div>
